import { FormEvent, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import api from "../../utils/api";
import { isUrisBrand } from "../../utils/brandLogo";
import { useAppSelector } from "../../store/hooks";

type PackageRow = {
  id: number;
  package_name: string;
  token_count: number;
  allow_surgical_guide: boolean;
  allow_add_implants: boolean;
  allow_custom_abutment: boolean;
  allow_printed_model: boolean;
  is_active: boolean;
};

type OptionFlags = {
  allow_surgical_guide: boolean;
  allow_add_implants: boolean;
  allow_custom_abutment: boolean;
  allow_printed_model: boolean;
};

const DEFAULT_FLAGS: OptionFlags = {
  allow_surgical_guide: true,
  allow_add_implants: true,
  allow_custom_abutment: true,
  allow_printed_model: true,
};

/** Example: Option 1 — abutment + printed model only (no guide planning/production). */
const OPTION_1_FLAGS: OptionFlags = {
  allow_surgical_guide: false,
  allow_add_implants: false,
  allow_custom_abutment: true,
  allow_printed_model: true,
};

const FLAG_LABELS: Array<{ key: keyof OptionFlags; label: string }> = [
  { key: "allow_surgical_guide", label: "Surgical Timing Guide" },
  { key: "allow_add_implants", label: "Add Implants" },
  { key: "allow_custom_abutment", label: "Custom Abutment" },
  { key: "allow_printed_model", label: "Printed Model" },
];

const asFlags = (row: PackageRow): OptionFlags => ({
  allow_surgical_guide: Boolean(row.allow_surgical_guide),
  allow_add_implants: Boolean(row.allow_add_implants),
  allow_custom_abutment: Boolean(row.allow_custom_abutment),
  allow_printed_model: Boolean(row.allow_printed_model),
});

const UrisPackageManager = () => {
  const { user } = useAppSelector((state) => state.auth);
  const allowed = isUrisBrand(user) && user?.role === "Side Admin";
  const [rows, setRows] = useState<PackageRow[]>([]);
  const [name, setName] = useState("");
  const [tokens, setTokens] = useState("");
  const [flags, setFlags] = useState<OptionFlags>(DEFAULT_FLAGS);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const res = await api.get("/api/uris-tokens/packages");
    setRows(res.data?.data || []);
  };

  useEffect(() => {
    if (!allowed) return;
    load().catch(() => setError("Could not load packages."));
  }, [allowed]);

  const createPackage = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api.post("/api/uris-tokens/packages", {
        package_name: name,
        token_count: Number(tokens),
        ...flags,
      });
      setName("");
      setTokens("");
      setFlags(DEFAULT_FLAGS);
      await load();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Could not save the package.";
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (row: PackageRow) => {
    await api.patch(`/api/uris-tokens/packages/${row.id}`, {
      is_active: !row.is_active,
    });
    await load();
  };

  const saveFlags = async (row: PackageRow, next: OptionFlags) => {
    setError("");
    try {
      await api.patch(`/api/uris-tokens/packages/${row.id}`, next);
      await load();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Could not update package options.";
      setError(message);
    }
  };

  if (!allowed) {
    return (
      <p className="text-[#c5c7cc]">
        Package manager is only available inside the Uris side admin account.
      </p>
    );
  }

  return (
    <>
      <Helmet>
        <title>Package Manager</title>
      </Helmet>
      <div className="text-white">
        <h1 className="text-2xl font-semibold">Package Manager</h1>
        <p className="mt-1 text-sm text-[#9a9ca3]">
          Token bundles for Uris labs. Included order options control which
          choices are selectable (vs greyed out) on the surgical guide form.
          Example: Option 1 with only Custom Abutment + Printed Model.
        </p>

        <form
          onSubmit={createPackage}
          className="mt-4 flex flex-col gap-3 rounded-2xl border border-[#6b2430] bg-[#141517] p-4"
        >
          <div className="flex flex-col gap-2 md:flex-row md:items-end">
            <label className="flex flex-1 flex-col gap-1 text-xs text-[#9a9ca3]">
              Package name
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="rounded-xl border border-[#3a3c42] bg-[#1a1b1f] px-3 py-2 text-sm text-white outline-none focus:border-[#c31c30]"
                placeholder="Option 1"
              />
            </label>
            <label className="flex w-full flex-col gap-1 text-xs text-[#9a9ca3] md:w-36">
              Tokens
              <input
                value={tokens}
                onChange={(e) => setTokens(e.target.value.replace(/[^\d]/g, ""))}
                className="rounded-xl border border-[#3a3c42] bg-[#1a1b1f] px-3 py-2 text-sm text-white outline-none focus:border-[#c31c30]"
                placeholder="10"
              />
            </label>
            <button
              type="button"
              onClick={() => setFlags(OPTION_1_FLAGS)}
              className="rounded-xl border border-[#3a3c42] px-3 py-2 text-xs text-[#c5c7cc] hover:border-[#c31c30] hover:text-white"
            >
              Use Option 1 preset
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-xl bg-[#e11d2e] px-4 py-2 text-sm font-medium text-white hover:bg-[#c31c30] disabled:opacity-60"
            >
              {saving ? "Saving…" : "Add package"}
            </button>
          </div>
          <div className="flex flex-wrap gap-3">
            {FLAG_LABELS.map(({ key, label }) => (
              <label
                key={key}
                className="inline-flex items-center gap-2 rounded-lg border border-[#3a3c42] bg-[#1a1b1f] px-3 py-2 text-xs text-[#c5c7cc]"
              >
                <input
                  type="checkbox"
                  checked={flags[key]}
                  onChange={(e) =>
                    setFlags((current) => ({
                      ...current,
                      [key]: e.target.checked,
                    }))
                  }
                  className="accent-[#e11d2e]"
                />
                {label}
              </label>
            ))}
          </div>
        </form>
        {error ? <p className="mt-3 text-sm text-[#ff8b96]">{error}</p> : null}

        <div className="mt-4 overflow-x-auto rounded-2xl border border-[#2e3036] bg-[#141517]">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-[#8b8d93]">
                <th className="px-4 py-3">Package</th>
                <th className="px-4 py-3">Tokens</th>
                <th className="px-4 py-3">Order options</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-[#8b8d93]">
                    No packages yet.
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const current = asFlags(row);
                  return (
                    <tr key={row.id} className="border-t border-[#2e3036]">
                      <td className="px-4 py-3 text-white">{row.package_name}</td>
                      <td className="px-4 py-3 text-[#ff8b96]">{row.token_count}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-2">
                          {FLAG_LABELS.map(({ key, label }) => (
                            <label
                              key={key}
                              className="inline-flex items-center gap-1.5 text-xs text-[#c5c7cc]"
                            >
                              <input
                                type="checkbox"
                                checked={current[key]}
                                onChange={(e) =>
                                  saveFlags(row, {
                                    ...current,
                                    [key]: e.target.checked,
                                  })
                                }
                                className="accent-[#e11d2e]"
                              />
                              {label}
                            </label>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => toggle(row)}
                          className={`rounded-full px-3 py-1 text-xs font-medium ${
                            row.is_active
                              ? "bg-[#163226] text-[#7dffa8]"
                              : "bg-[#2a2b31] text-[#9a9ca3]"
                          }`}
                        >
                          {row.is_active ? "Active" : "Hidden"}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default UrisPackageManager;
